import {CurrencyInput, FormControl, FormField} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';
import {isNumber, isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useDebouncedCallback} from 'shared/web';

import {roundToTwoDecimals} from '../../RentalOrderHeader/utils/roundToTwoDecimals';
import {RentVehicleFormValues} from '../types/RentVehicleFormValues';

interface DiscountPriceRowProps extends RequiredTestIdProps {
  control: FormControl<RentVehicleFormValues>;
  formApi: UseFormReturn<RentVehicleFormValues>;
}

export function DiscountPriceRow(props: DiscountPriceRowProps) {
  const [discount, isCustomPriceEnabled] = useWatch({
    control: props.control,
    name: [`rentPrice.discount.discount`, 'isCustomPriceEnabled'],
  });

  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();

  const handleRecalculateAfterDiscountChange = useDebouncedCallback(
    (percentageDiscount: number | null) => {
      props.formApi.clearErrors('rentPrice.discount.percent');
      const absoluteVatRate = vatRatesByCode?.[discount?.vatType ?? '']?.rate;
      const totalPriceWithoutVat = props.formApi.getValues('rentPrice.rentalPrice.withoutVat');

      if (isNil(percentageDiscount) || isNil(absoluteVatRate) || isNil(totalPriceWithoutVat)) {
        return;
      }

      if (percentageDiscount < 0) {
        props.formApi.setError('rentPrice.discount.percent', {
          type: 'manual',
          message: i18n.t('page.rentalOrder.labels.discountPercentMustBePositive'),
        });

        return;
      }

      const rate = parseFloat(absoluteVatRate);
      const newPriceWithoutVat = totalPriceWithoutVat * (percentageDiscount / 100);
      const newPriceWithVat = newPriceWithoutVat * (1 + rate / 100);
      const newVatPrice = newPriceWithVat - newPriceWithoutVat;

      props.formApi.setValue(`rentPrice.discount.discount.withoutVat`, newPriceWithoutVat);
      props.formApi.setValue(`rentPrice.discount.discount.withVat`, newPriceWithVat);
      props.formApi.setValue(`rentPrice.discount.discount.vat`, newVatPrice);
    },
    200
  );

  const handleRecalculateAfterVatRateChange = (value: string | number | null) => {
    if (!isString(value)) {
      return;
    }

    const discount = props.formApi.getValues('rentPrice.discount.discount');
    const absoluteVatRate = vatRatesByCode?.[value ?? discount?.vatType ?? 'S']?.rate;
    const priceWithoutVat = discount?.withoutVat ?? 0;
    const rate = parseFloat(absoluteVatRate ?? '0');

    const newPriceWithVat = roundToTwoDecimals(priceWithoutVat * (1 + rate / 100));
    const newVatPrice = roundToTwoDecimals(newPriceWithVat - priceWithoutVat);

    props.formApi.setValue(`rentPrice.discount.discount.withVat`, newPriceWithVat);
    props.formApi.setValue(`rentPrice.discount.discount.vat`, newVatPrice);
  };

  props.formApi.register('rentPrice.discount.discount.withoutVat');
  props.formApi.register('rentPrice.discount.discount.withVat');
  props.formApi.register('rentPrice.discount.discount.vat');

  return (
    <Grid columns={4}>
      <Grid columns={4} spacing={2}>
        <FormField
          control={props.control}
          type="number"
          name="rentPrice.discount.percent"
          label={i18n.t('page.rentalOrder.labels.discountPercent')}
          suffix="%"
          isDisabled={isCustomPriceEnabled}
          onChange={handleRecalculateAfterDiscountChange}
          data-testid={suffixTestId('discountPercent', props)}
        />

        <GridItem span={3}>
          <CurrencyInput
            value={isNumber(discount?.withoutVat) ? -discount.withoutVat : null}
            currency={tenantCurrency}
            data-testid={suffixTestId('discountPriceWithoutVat', props)}
            decimalPlaces={2}
            label={i18n.t('page.rentalOrder.labels.discountPriceWithoutVat')}
            isDisabled
          />
        </GridItem>
      </Grid>

      <CurrencyInput
        value={isNumber(discount?.withVat) ? -discount.withVat : null}
        currency={tenantCurrency}
        data-testid={suffixTestId('discountPriceWithVat', props)}
        decimalPlaces={2}
        label={i18n.t('page.rentalOrder.labels.discountPriceWithVat')}
        isDisabled
      />

      <CurrencyInput
        value={isNumber(discount?.vat) ? -discount.vat : null}
        currency={tenantCurrency}
        data-testid={suffixTestId('discountPriceVat', props)}
        decimalPlaces={2}
        label={`${i18n.t('entity.vehicle.labels.vat')} ${formatCurrencySymbol(tenantCurrency)}`}
        isDisabled
      />

      <FormField
        control={props.control}
        type="choice"
        name="rentPrice.discount.discount.vatType"
        label={i18n.t('entity.vehicle.labels.vat')}
        options={vatRatesOptions}
        isNotClearable
        isDisabled={isCustomPriceEnabled}
        onChange={handleRecalculateAfterVatRateChange}
        data-testid={suffixTestId('discountPriceVatRate', props)}
      />
    </Grid>
  );
}
